/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Sur une page collection, stocke l'URL + le nom court Shopify de la collection lors d'un clic sur un produit
if (window.location.pathname.startsWith('/collections/')) {
  document.querySelectorAll('a[href*="/products/"]').forEach(link => {
    link.addEventListener('click', function() {
      var breadcrumbColl = document.querySelector('.breadcrumb__item a[data-shopify-collection-title]');
      var collectionTitle = breadcrumbColl
        ? breadcrumbColl.getAttribute('data-shopify-collection-title')
        : document.title;
      sessionStorage.setItem('last_collection_url', window.location.pathname);
      sessionStorage.setItem('last_collection_title', collectionTitle);
    });
  });
}

// Sur une page produit, met à jour ou insère dynamiquement le breadcrumb collection depuis sessionStorage
if (window.location.pathname.startsWith('/products/')) {
  document.addEventListener('DOMContentLoaded', function() {
    const colUrl = sessionStorage.getItem('last_collection_url');
    const colTitle = sessionStorage.getItem('last_collection_title');
    const breadcrumbList = document.querySelector('.breadcrumb__list');
    const existingCollectionItem = document.querySelector('[data-breadcrumb-collection-item]');
    if (colUrl && colTitle && breadcrumbList) {
      if (existingCollectionItem) {
        // Met à jour le crumb si déjà présent
        const link = existingCollectionItem.querySelector('.breadcrumb__link');
        if (link) {
          link.href = colUrl;
          link.textContent = colTitle;
          link.setAttribute('data-shopify-collection-title', colTitle);
        }
      } else {
        // Sinon, le crée et l'insère juste après l'accueil
        const homeItem = breadcrumbList.querySelector('.breadcrumb__item:first-child');
        if (homeItem) {
          const newLi = document.createElement('li');
          newLi.className = 'breadcrumb__item';
          newLi.setAttribute('data-breadcrumb-collection-item', '');
          const newA = document.createElement('a');
          newA.className = 'breadcrumb__link';
          newA.href = colUrl;
          newA.textContent = colTitle;
          newA.setAttribute('data-shopify-collection-title', colTitle);
          newLi.appendChild(newA);
          homeItem.insertAdjacentElement('afterend', newLi);
        }
      }
    }
  });
}


// =================================================================================
// SCRIPT DE GESTION DE LA BARRE DE COMPARAISON STICKY (+ Drawer/Popover)
// =================================================================================

// --- INIT ---
document.addEventListener('DOMContentLoaded', initializeCompareScript);
document.addEventListener('shopify:section:load', initializeCompareScript);
document.addEventListener('sections:reloaded', initializeCompareScript);

// --- STORAGE ---
const STORAGE_KEY = 'shopifyProductCompareList';
const maxCompare = 3;

function getCompareList() {
  try {
    const list = localStorage.getItem(STORAGE_KEY);
    return list ? JSON.parse(list) : [];
  } catch {
    return [];
  }
}
function setCompareList(list) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch {}
}

// --- UI : checkboxes ---
function syncCheckboxesWithStorage() {
  const productObjectsInStorage = getCompareList();
  const productIdsInStorage = productObjectsInStorage.map(p => p.id);
  const checkboxesOnPage = document.querySelectorAll('.compare-checkbox');
  checkboxesOnPage.forEach(cb => {
    cb.checked = productIdsInStorage.includes(cb.dataset.productId);
  });
}

// --- UI : barre sticky ---
function refreshCompareBar() {
  const productObjectsInStorage = getCompareList();
  const currentCount = productObjectsInStorage.length;
  const compareBar = document.getElementById('compare-bar');
  const compareProductsContainer = document.getElementById('compare-products');
  const compareNowButtons = document.querySelectorAll('.compare-now-btn');

  if (compareBar) {
    currentCount > 0 ? compareBar.removeAttribute('hidden') : compareBar.setAttribute('hidden', '');
  }

  const buttonText = currentCount > 0 ? `Comparer (${currentCount}/${maxCompare})` : 'Comparer maintenant';
  compareNowButtons.forEach(btn => {
    btn.innerHTML = buttonText;
    btn.disabled = (currentCount < 2);
  });

  if (compareProductsContainer) {
    compareProductsContainer.innerHTML = '';
    productObjectsInStorage.forEach(product => {
      const stickyBlock = document.createElement('div');
      stickyBlock.className = 'product-sticky-form__content-block compare-sticky-block';
      stickyBlock.innerHTML = `
        <div class="product-sticky-form__image-wrapper">
          <img src="${product.imageSrc}" alt="${product.title}" class="product-sticky-form__image" loading="lazy">
        </div>
        <div class="product-sticky-form__info">
          <div class="product-sticky-form__bottom-info">
            <span class="product-sticky-form__title">${product.title}</span>
            <span class="square-separator square-separator--subdued"></span>
            <span class="product-sticky-form__price">${product.priceHtml || ''}</span>
          </div>
        </div>
        <button type="button" class="product-sticky-form__remove" data-remove-id="${product.id}" aria-label="Retirer ce produit" title="Retirer">✕</button>
      `;
      compareProductsContainer.appendChild(stickyBlock);
    });
  }

  // Désactivation des checkboxes si limite atteinte
  const allCheckboxes = document.querySelectorAll('.compare-checkbox');
  allCheckboxes.forEach(cb => {
    if (!cb.checked) {
      cb.disabled = (currentCount >= maxCompare);
    }
    const card = cb.closest('.product-item');
    if (card) {
      card.classList.toggle('product-item--compare', cb.checked);
    }
  });
}

// --- INIT GLOBALE ---
function initializeCompareScript() {
  syncCheckboxesWithStorage();
  refreshCompareBar();
  setupEventListeners();
}

// --- OUVERTURE drawer/popover ---
function openCompareUI() {
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const drawer  = document.getElementById('product-compare-drawer');
  const popover = document.getElementById('product-compare-popover');

  if (isMobile) {
    if (popover && !popover.hasAttribute('open')) popover.setAttribute('open','');
    document.documentElement.dispatchEvent(new CustomEvent('popover:open', { detail: { id: 'product-compare-popover' }}));
  } else {
    if (drawer && !drawer.hasAttribute('open')) drawer.setAttribute('open','');
    document.documentElement.dispatchEvent(new CustomEvent('drawer:open', { detail: { id: 'product-compare-drawer' }}));
  }
}

// --- RENDU drawer/popover ---
function renderCompareUIFromStorage() {
  const productDataList = getCompareList();
  const containers = document.querySelectorAll('.compare-container');
  if (!containers.length) return;

  if (!productDataList.length) {
    containers.forEach(c => c.innerHTML = '<p style="padding:1rem;">Aucun produit à comparer pour le moment.</p>');
    return;
  }

  const labelMap = {
    'poids': 'Poids','connectivite': 'Connectivité','dimensions_en_usage':'Dimensions en usage',
    'dimensions_plie':'Dimensions plié','inclinaison':'Inclinaison','indicateurs':'Indicateurs',
    'mesures_cardiaques':'Mesures cardiaques','pliable':'Pliable','poids_max_utilisateur':'Poids max utilisateur',
    'programmes':'Programmes','puissance_moteur':'Puissance moteur','surface_de_course':'Surface de course',
    'systeme_d_amortissement':'Système d\'amortissement','vitesses_max':'Vitesse max','roue_d_inertie':'Roue d\'inertie',
    'resistance':'Résistance','auto_alimente':'Auto-alimenté'
  };

  const attributesToDisplay = new Set();
  productDataList.forEach(p => {
    if (p && p.features) for (const k in p.features) attributesToDisplay.add(k);
  });

  let html = '<div class="compare-products-header">';
  productDataList.forEach(p => {
    html += `
      <div class="compare-products-header__cell">
        <img src="${p.imageSrc}" alt="${p.title}" class="compare-products-header__image" loading="lazy">
        <div class="compare-products-header__title">${p.title || ''}</div>
        <div class="compare-products-header__price">${p.priceHtml || ''}</div>
      </div>`;
  });
  html += '</div>';

  attributesToDisplay.forEach(key => {
    const label = labelMap[key] || key;
    html += `<div class="compare-feature-block">`;
    html += `<div class="compare-feature-block__header">${label}</div>`;
    html += `<div class="compare-feature-block__values">`;
    productDataList.forEach(p => {
      let value = '<span class="compare-table__no-value">-</span>';
      const raw = p?.features?.[key];
      if (raw != null) {
        if (typeof raw === 'object' && raw.value != null) {
          value = `${raw.value} ${raw.unit || ''}`.trim();
        } else {
          value = String(raw);
        }
      }
      html += `<div class="compare-feature-block__cell">${value}</div>`;
    });
    html += `</div></div>`;
  });

  containers.forEach(c => c.innerHTML = html);
}

// --- EVENTS ---
let eventListenersAttached = false;

function setupEventListeners() {
  if (eventListenersAttached) return;

  // Checkbox comparer
  document.body.addEventListener('change', (event) => {
    if (event.target.matches('.compare-checkbox')) handleCompareChange(event.target);
  });

  // Clics délégués
  document.body.addEventListener('click', (event) => {
    // Retirer un produit
    if (event.target.matches('.product-sticky-form__remove')) {
      event.preventDefault();
      handleRemoveClick(event.target);
      return;
    }

    // Ouvrir drawer/popover
    const openBtn = event.target.closest('.compare-now-btn');
    if (openBtn) {
      event.preventDefault();
      if (getCompareList().length >= 2) {
        openCompareUI();
        renderCompareUIFromStorage();
      }
      return;
    }

    // Tout effacer
    const clearBtn = event.target.closest('#compare-clear-btn');
    if (clearBtn) {
      event.preventDefault();
      setCompareList([]);
      refreshCompareBar();
      renderCompareUIFromStorage();
      syncCheckboxesWithStorage();

      // 🔥 Supprime aussi la classe CSS de sélection et reset les checkboxes
      document.querySelectorAll('.product-item').forEach(card => {
        card.classList.remove('product-item--compare');
        const cb = card.querySelector('.compare-checkbox');
        if (cb) {
          cb.checked = false;
          cb.disabled = false;
        }
      });
      return;
    }
  });

  // Sync multi-onglets
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      refreshCompareBar();
      renderCompareUIFromStorage();
    }
  });

  eventListenersAttached = true;
}

// --- HANDLERS ---
function handleCompareChange(checkbox) {
  const productId = checkbox.dataset.productId;
  const card = checkbox.closest('.product-item');
  let currentList = getCompareList();

  if (checkbox.checked) {
    if (currentList.length < maxCompare && !currentList.find(p => p.id === productId)) {
      const imgElement = card?.querySelector('.product-item__primary-image');
      currentList.push({
        id: productId,
        title: card?.querySelector('.product-item-meta__title')?.textContent.trim() || '',
        priceHtml: card?.querySelector('.product-item-meta__price-list-container')?.innerHTML || '',
        imageSrc: imgElement ? (imgElement.src || imgElement.dataset.src) : '',
        features: collectFeaturesData(card)
      });
    } else {
      if (currentList.length >= maxCompare) checkbox.checked = false;
    }
  } else {
    currentList = currentList.filter(p => p.id !== productId);
  }

  setCompareList(currentList);
  refreshCompareBar();
}

function handleRemoveClick(removeButton) {
  const idToRemove = removeButton.dataset.removeId;
  let currentList = getCompareList();
  currentList = currentList.filter(p => p.id !== idToRemove);
  setCompareList(currentList);
  initializeCompareScript();
  renderCompareUIFromStorage();
}

function collectFeaturesData(card) {
  if (card && card.dataset.featuresJson) {
    try {
      return JSON.parse(card.dataset.featuresJson);
    } catch (e) {
      console.error("Erreur parsing data-features-json:", e);
      return {};
    }
  }
  return {};
}

//====================================================================================
// --- FAV STORE SCRIPT DRAWER
//====================================================================================

(function(){
  // ===== Données magasins =====
  const HANDLE_TO={
    "67838968004":{name:"Decathlon Saint-Denis",slug:"decathlon-saint-denis"},
    "63769542852":{name:"Decathlon Saint-Pierre",slug:"decathlon-saint-pierre"},
    "67839033540":{name:"Decathlon Sainte-Suzanne",slug:"decathlon-sainte-suzanne"}
  };
  const ALL_HANDLES=Object.keys(HANDLE_TO);
  const SELECTOR_FORMS=['#fav-store-form-desktop','#fav-store-form-mobile'];

  let initialized=false, uiAttached=false;

  // ===== LS helper =====
  const LS={
    get:k=>{try{return localStorage.getItem(k)}catch(_){return null}},
    set:(k,v)=>{try{localStorage.setItem(k,v)}catch(_){}} 
  };

  // ===== Notification =====
  function ensureFavNotice(){
    let el=document.getElementById('fav-store-notice');
    if(!el){
      el=document.createElement('div');
      el.id='fav-store-notice';
      el.className='fav-store-notification';
      el.setAttribute('role','status');
      el.setAttribute('aria-live','polite');
      el.innerHTML='<div class="fav-store-notification__inner"><span id="fav-store-notice-text">Magasin favori enregistré</span><button type="button" class="fav-store-notification__close" aria-label="Fermer">×</button></div>';
      document.body.appendChild(el);
      el.querySelector('.fav-store-notification__close').addEventListener('click',hideFavNotice);
    }
    return el;
  }
  function showFavNotice(text,{duration=5000}={}){
    const el=ensureFavNotice();
    const txt=el.querySelector('#fav-store-notice-text');
    if(txt&&text) txt.textContent=text;
    el.classList.add('show');
    clearTimeout(el.__hideTimer);
    if(duration>0) el.__hideTimer=setTimeout(hideFavNotice,duration);
  }
  function hideFavNotice(){
    const el=document.getElementById('fav-store-notice');
    if(el) el.classList.remove('show');
  }

  // ===== Utils =====
  function handleize(s){
    return (s||'').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
      .replace(/&/g,'and').replace(/[^a-z0-9]+/g,'-')
      .replace(/(^-|-$)/g,'').replace(/--+/g,'-');
  }
  async function updateCartAttributes(attrs){
    const res=await fetch('/cart/update.js',{
      method:'POST',
      headers:{'Content-Type':'application/json'},
      credentials:'same-origin',
      body:JSON.stringify({attributes:attrs})
    });
    if(!res.ok) throw new Error('Cart update failed');
    return res.json();
  }

  // ===== Core: set favorite (via clic bouton) =====
  async function setFavorite(handle){
    const meta=HANDLE_TO[handle]||{};
    const name=meta.name||'';
    const slug=meta.slug||handleize(name);
    try{
      LS.set('favorite_store_handle',handle);
      LS.set('favorite_store_name',name);
      LS.set('favorite_store_slug',slug);

      await updateCartAttributes({
        favorite_store_handle:handle,
        favorite_store_name:name,
        favorite_store_slug:slug
      });

      window.dispatchEvent(new Event('favorite-store:changed'));
      showFavNotice(`Magasin favori enregistré : ${name}`,{duration:5000});

      setTimeout(()=>{
        document.querySelector('#fav-store-drawer [data-action="close"]')?.click();
        document.querySelector('#fav-store-popover [data-action="close"]')?.click();
      },200);
    }catch(e){
      console.error('[FavStore] setFavorite error',e);
      const msgDesktop=document.getElementById('fav-store-msg-desktop');
      const msgMobile=document.getElementById('fav-store-msg-mobile');
      [msgDesktop,msgMobile].forEach(m=>{
        if(m){ m.textContent="Une erreur technique est survenue."; m.style.color="red"; }
      });
    }
  }

  // ===== UI helpers =====
  function markSelectionVisually(form,value){
    if(!form) return;
    const radio=form.querySelector(`input[type="radio"][name="fav_store"][value="${value}"]`);
    if(radio){
      radio.checked=true;
      form.querySelectorAll('.fav-store-card-content').forEach(d=>d.classList.remove('selected'));
      radio.parentElement?.querySelector('.fav-store-card-content')?.classList.add('selected');
      const btn=form.querySelector('.fav-store-choose-btn');
      if(btn) btn.disabled=false;
    }
  }
  function hydrateUI(){
    const handle=LS.get('favorite_store_handle');
    if(handle&&ALL_HANDLES.includes(handle)){
      SELECTOR_FORMS.forEach(sel=>{
        const form=document.querySelector(sel);
        markSelectionVisually(form,handle);
      });
    }
  }

  // ===== Listeners =====
  function attachUIHandlers(){
    if(uiAttached) return;
    uiAttached=true;

    document.addEventListener('change',e=>{
      if(e.target && e.target.matches('input[type="radio"][name="fav_store"]')){
        const radio=e.target;
        const form=radio.closest('form');
        markSelectionVisually(form,radio.value);
      }
    });

    document.addEventListener('click',e=>{
      const submitButton=e.target.closest('.fav-store-choose-btn');
      if(!submitButton) return;
      const form=submitButton.closest('form');
      if(!form) return;
      const selectedRadio=form.querySelector('input[type="radio"]:checked[name="fav_store"]');
      if(!selectedRadio){
        const msg=form.id.includes('desktop') ? document.getElementById('fav-store-msg-desktop') : document.getElementById('fav-store-msg-mobile');
        if(msg){ msg.textContent="Erreur : veuillez sélectionner un magasin."; msg.style.color="red"; }
        return;
      }
      submitButton.setAttribute('aria-busy','true');
      submitButton.disabled=true;
      setFavorite(selectedRadio.value).finally(()=>{
        submitButton.removeAttribute('aria-busy');
      });
    });

    // Nettoyage basique sur fermeture (si le thème crée un backdrop)
    document.addEventListener('click',e=>{
      const closeBtn=e.target.closest('#fav-store-drawer [data-action="close"], #fav-store-popover [data-action="close"], .modal-backdrop, [data-dismiss="modal"]');
      if(!closeBtn) return;
      const backdrop=document.querySelector('.modal-backdrop');
      if(backdrop && !backdrop.dataset.original) backdrop.remove();
      document.body.style.overflow='';
    },{capture:true});
  }

  // ===== Init =====
  function init(){
    if(initialized) return;
    initialized=true;
    hydrateUI();
    attachUIHandlers();
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',init);
  else init();

  // Debug minimal (utile pour support)
  window.favStoreDebug={
    checkElements:()=>{console.log('Drawer:',document.getElementById('fav-store-drawer'));console.log('Popover:',document.getElementById('fav-store-popover')); }
  };
})();


//====================================================================================================================================================
// REDIRECTION QR CODE BALISAGE
//====================================================================================================================================================

(function() {
 
  if (!window.location.pathname.startsWith('/qr/')) return;

  const match = window.location.pathname.match(/^\/qr\/(\d+)$/);
  if (!match) return;
  const code = match[1];

  
  document.body.innerHTML = `<div style="position:fixed;top:0;left:0;width:100%;height:100%;background:#fff;display:flex;align-items:center;justify-content:center;z-index:9999">
      <div style="text-align:center">
        <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true" focusable="false" style="margin:0 auto 15px;display:block"><g transform="rotate(-40 24 24)"><g transform="translate(0 9.6) scale(1 0.6)"><g><animateTransform attributeName="transform" type="rotate" from="-145 24 24" to="-505 24 24" dur="1.4s" calcMode="spline" keyTimes="0;1" keySplines=".62 0 .38 1" repeatCount="indefinite"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="6.00" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="0.000" stroke-opacity="1.000"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.92" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-1.571" stroke-opacity="0.984"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.84" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-3.142" stroke-opacity="0.968"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.76" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-4.712" stroke-opacity="0.952"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.68" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-6.283" stroke-opacity="0.937"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.60" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-7.854" stroke-opacity="0.921"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.52" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-9.425" stroke-opacity="0.905"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.44" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-10.996" stroke-opacity="0.889"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.37" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-12.566" stroke-opacity="0.873"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.29" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-14.137" stroke-opacity="0.857"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.21" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-15.708" stroke-opacity="0.841"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.13" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-17.279" stroke-opacity="0.825"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="5.05" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-18.850" stroke-opacity="0.810"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.97" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-20.420" stroke-opacity="0.794"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.89" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-21.991" stroke-opacity="0.778"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.81" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-23.562" stroke-opacity="0.762"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.73" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-25.133" stroke-opacity="0.746"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.65" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-26.704" stroke-opacity="0.730"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.57" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-28.274" stroke-opacity="0.714"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.49" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-29.845" stroke-opacity="0.698"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.41" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-31.416" stroke-opacity="0.683"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.33" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-32.987" stroke-opacity="0.667"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.25" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-34.558" stroke-opacity="0.651"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.17" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-36.128" stroke-opacity="0.635"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.10" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-37.699" stroke-opacity="0.619"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="4.02" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-39.270" stroke-opacity="0.603"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.94" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-40.841" stroke-opacity="0.587"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.86" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-42.412" stroke-opacity="0.571"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.78" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-43.982" stroke-opacity="0.556"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.70" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-45.553" stroke-opacity="0.540"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.62" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-47.124" stroke-opacity="0.524"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.54" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-48.695" stroke-opacity="0.508"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.46" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-50.265" stroke-opacity="0.492"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.38" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-51.836" stroke-opacity="0.476"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.30" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-53.407" stroke-opacity="0.460"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.22" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-54.978" stroke-opacity="0.444"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.14" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-56.549" stroke-opacity="0.429"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="3.06" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-58.119" stroke-opacity="0.413"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.98" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-59.690" stroke-opacity="0.397"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.90" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-61.261" stroke-opacity="0.381"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.83" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-62.832" stroke-opacity="0.365"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.75" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-64.403" stroke-opacity="0.349"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.67" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-65.973" stroke-opacity="0.333"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.59" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-67.544" stroke-opacity="0.317"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.51" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-69.115" stroke-opacity="0.302"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.43" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-70.686" stroke-opacity="0.286"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.35" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-72.257" stroke-opacity="0.270"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.27" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-73.827" stroke-opacity="0.254"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.19" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-75.398" stroke-opacity="0.238"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.11" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-76.969" stroke-opacity="0.222"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="2.03" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-78.540" stroke-opacity="0.206"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.95" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-80.111" stroke-opacity="0.190"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.87" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-81.681" stroke-opacity="0.175"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.79" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-83.252" stroke-opacity="0.159"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.71" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-84.823" stroke-opacity="0.143"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.63" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-86.394" stroke-opacity="0.127"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.56" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-87.965" stroke-opacity="0.111"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.48" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-89.535" stroke-opacity="0.095"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.40" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-91.106" stroke-opacity="0.079"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.32" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-92.677" stroke-opacity="0.063"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.24" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-94.248" stroke-opacity="0.048"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.16" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-95.819" stroke-opacity="0.032"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.08" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-97.389" stroke-opacity="0.016"/><circle cx="24" cy="24" r="20" fill="none" stroke="#3743b3" stroke-width="1.00" stroke-linecap="round" stroke-dasharray="1.571 124.093" stroke-dashoffset="-98.960" stroke-opacity="0.000"/></g></g></g></svg>
        <p style="margin:0;color:#666;font-family:sans-serif">Redirection...</p>
      </div>
    </div>`;

 
  fetch('https://redirection-yqzu.onrender.com/find-product?modelCode=' + encodeURIComponent(code))
    .then(res => res.json())
    .then(data => {
      if (data.url) {
        window.location.replace(data.url);
      } else {
        // Fallback si aucun produit trouvé
        window.location.replace('/collections/');
      }
    })
    .catch(() => {
      window.location.replace('/');
    });
})();


//===============================================================================================================================================
// Suppression cache pour éviter le bug des produits épuisés sur tout le site
//===============================================================================================================================================

(() => {
  const EXP = new Date('2025-10-01');            // fenêtre courte
  const LKEY = 'swCleanup_v2_done';              // flag persistant
  const SKEY = 'swCleanup_v2_session';           // flag session (anti-boucle)
  const RKEY = 'swCleanup_v2_reloadCount';       // compteur reload (sécurité)

  // 0) Garde-fous rapides
  if (new Date() > EXP) return;                  // expiré
  if (location.pathname.startsWith('/admin')) return;  // pas dans l'admin thème
  // Évite d'impacter les scans/headless
  const ua = navigator.userAgent || '';
  const isBot = /Headless|Lighthouse|bot|crawl|spider/i.test(ua) || navigator.webdriver;
  if (isBot) return;

  // 1) Storage helpers (Safari privé/quotas peuvent throw)
  const safeStorage = {
    getL(k){ try { return localStorage.getItem(k); } catch { return null; } },
    setL(k,v){ try { localStorage.setItem(k,v); return true; } catch { return false; } },
    getS(k){ try { return sessionStorage.getItem(k); } catch { return null; } },
    setS(k,v){ try { sessionStorage.setItem(k,v); return true; } catch { return false; } },
    removeL(k){ try { localStorage.removeItem(k); } catch {} }
  };

  // 2) Anti-répétition
  if (safeStorage.getL(LKEY) === '1' || safeStorage.getS(SKEY) === '1') return;

  // 3) Compteur reload anti-boucle
  const reloadCount = parseInt(safeStorage.getS(RKEY) || '0', 10);
  if (reloadCount >= 2) { // si 2 reloads d'affilée, on arrête
    safeStorage.setS(SKEY, '1');
    return;
  }

  // 4) Exécution asynchrone
  (async () => {
    try {
      const tasks = [];
      let swCount = 0, cacheNames = [];

      // A) Service Workers
      if ('serviceWorker' in navigator) {
        tasks.push(
          navigator.serviceWorker.getRegistrations()
            .then(regs => {
              swCount = regs.length;
              return Promise.allSettled(regs.map(r => r.unregister()));
            })
        );
      }

      // B) CacheStorage
      if (window.caches && caches.keys) {
        tasks.push(
          caches.keys().then(names => {
            cacheNames = names.slice();
            return Promise.allSettled(names.map(n => caches.delete(n)));
          })
        );
      }

      // C) Exécuter
      await Promise.allSettled(tasks);

      console.log(
        '✅ Cleanup SW/cache (auto):',
        { swUnregisterTried: swCount, cachesFound: cacheNames }
      );

      // 5) Flags + reload doux
      safeStorage.setL(LKEY, '1');           // ne rejouera plus pour ce navigateur
      safeStorage.setS(SKEY, '1');           // anti double exécution sur cette session
      safeStorage.setS(RKEY, String(reloadCount + 1));

      // reload sans revalidation de l'historique
      location.replace(location.href);
    } catch (e) {
      console.warn('Cleanup error:', e);
      // Éviter de retenter en boucle si exception inconnue
      safeStorage.setS(SKEY, '1');
    }
  })();
})();

//=========================================================================================
// progress bar product page sur mobile
//========================================================================================
(function () {
  function initLinearPager(pagerEl, flickityInstance) {
    var barEl = pagerEl.querySelector('.pager-progress__bar');
    if (!barEl) return;

    function updateBar() {
      var total = flickityInstance.slides.length;

      if (total <= 1) {
        // 1 seule image => barre pleine
        barEl.style.transform = 'scaleX(1)';
        return;
      }

      var index = flickityInstance.selectedIndex || 0;

      // ratio de progression entre 0 et 1
      // 0 = première image, 1 = dernière image
      var progressRatio = index / (total - 1);

      // Application via transform pour éviter le bug Safari sur width %
      barEl.style.transform = 'scaleX(' + progressRatio + ')';
    }

    // Position initiale
    updateBar();

    // Quand on change de slide
    flickityInstance.on('select', updateBar);
    flickityInstance.on('cellChange', updateBar);

    // Optionnel : si la taille du rail change (rotation écran, etc.)
    window.addEventListener('resize', updateBar);
  }

  function attachAllLinearPagers() {
    var pagerBlocks = document.querySelectorAll('.pager-progress');
    if (!pagerBlocks.length) return;

    pagerBlocks.forEach(function (pagerEl) {
      var controlsEl = pagerEl.closest('flickity-controls');
      if (!controlsEl) return;

      var carouselId = controlsEl.getAttribute('controls');
      if (!carouselId) return;

      var carouselEl = document.getElementById(carouselId);
      if (!carouselEl) return;

      var flickityRoot = carouselEl.closest('.flickity-enabled') || carouselEl;
      if (!flickityRoot) return;

      if (!window.ThemeFlickity || !window.ThemeFlickity.data) return;
      var instance = window.ThemeFlickity.data(flickityRoot);
      if (!instance) return;

      initLinearPager(pagerEl, instance);
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    attachAllLinearPagers();
    // deuxième passe pour couvrir le cas où Flickity se monte juste après
    setTimeout(attachAllLinearPagers, 200);
  });
})();


// ================================= ENVIRONNEMENT  ===========================================

/* assets/env-widget.js
 * Widget Environnement (metafield custom.environnement)
 * - Multi-instances (drawer + popover + plusieurs sections)
 * - Affiche depuis Liquid si dispo, sinon proxy /apps/reviews?sku=...
 * - Compat: proxy renvoie soit {environment}, soit {decathlon}
 */

(() => {
  const DEFAULT_PROXY = '/apps/reviews';

  const safeJsonParse = (s) => { try { return JSON.parse(s); } catch { return null; } };

  const escapeHtml = (s) => String(s || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const escapeAttr = (s) => escapeHtml(s).replaceAll('\n', ' ');

  const uniq = (arr) => [...new Set((arr || []).filter(Boolean))];

  async function fetchJSON(url) {
    const r = await fetch(url, { credentials: 'omit' });
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
  }

  function buildProxyUrl(proxyBase, sku) {
    const u = new URL(proxyBase || DEFAULT_PROXY, location.origin);
    u.searchParams.set('sku', sku);
    return u.toString();
  }

  function extractPayloadFromProxy(res) {
    return res?.environment || res?.decathlon || res?.payload || null;
  }

  function isValidEnvPayload(data) {
    if (!data || typeof data !== 'object') return false;
    const envData = Array.isArray(data.environmentalData) ? data.environmentalData : [];
    const agec = Array.isArray(data.agec) ? data.agec : [];
    return envData.length > 0 || agec.length > 0;
  }

  function setStatus(ROOT, msg) {
    const el = ROOT.querySelector('.env-status');
    if (el) el.textContent = msg || '';
  }

  function renderEnv(ROOT, data) {
    const actionsEl = ROOT.querySelector('.env-actions');
    const agecEl = ROOT.querySelector('.env-agec');
    if (!actionsEl || !agecEl) return;

    const envData = Array.isArray(data?.environmentalData) ? data.environmentalData : [];
    const agec = Array.isArray(data?.agec) ? data.agec : [];

    if (!envData.length && !agec.length) {
      actionsEl.innerHTML = `<p>Aucune donnée environnementale disponible.</p>`;
      agecEl.innerHTML = '';
      return;
    }

    actionsEl.innerHTML = envData.map((it) => {
      const actionType = it?.actionType || '';
      const actionName = it?.actionName || '';
      const percentage = it?.percentage;
      const imgUrl = it?.image?.url || '';
      const legal = it?.legalPhrase || '';
      const txt = it?.communicationText || '';

      const pct = Number.isFinite(Number(percentage)) ? `${Number(percentage)}%` : '';

      return `
        <div class="env-item">
          <div class="env-item__head">
            <div class="env-item__name">${escapeHtml(actionType)}${actionName ? ` — ${escapeHtml(actionName)}` : ''}</div>
            <div class="env-item__pct">${pct}</div>
          </div>
          ${legal ? `<p class="env-item__legal">${escapeHtml(legal)}</p>` : ''}
          ${txt ? `<p class="env-item__text">${escapeHtml(txt)}</p>` : ''}
          ${imgUrl ? `<img class="env-item__img" src="${encodeURI(imgUrl)}" alt="${escapeAttr(actionName || actionType)}">` : ''}
        </div>
      `;
    }).join('');

    // AGEC (pays + date)
    const countries = [];
    const lastMod = [];

    function walk(node) {
      if (!node || typeof node !== 'object') return;
      if (Array.isArray(node)) { node.forEach(walk); return; }

      const name = node.name || '';
      const values = node.values;

      if (
        name === 'Pays de confection' ||
        name === 'Pays de tissage/tricotage' ||
        name === 'Pays de teinture et impression'
      ) {
        if (Array.isArray(values)) values.forEach(v => countries.push(`${name}: ${v}`));
      }

      if (name === 'Date de dernière modification') {
        if (Array.isArray(values)) values.forEach(v => lastMod.push(v));
      }

      if (Array.isArray(node.agecProperties)) node.agecProperties.forEach(walk);
    }

    agec.forEach(walk);

    const c = uniq(countries);
    const d = uniq(lastMod);

    agecEl.innerHTML = `
      ${c.length ? `<div><strong>Fabrication</strong><br>${c.map(escapeHtml).join('<br>')}</div>` : ''}
      ${d.length ? `<div style="margin-top:8px; opacity:.85;">Dernière mise à jour: ${d.map(escapeHtml).join(', ')}</div>` : ''}
    `;
  }

  function readInlineJson(ROOT) {
    const script = ROOT.querySelector('script.env-initial-json[type="application/json"]');
    if (script) {
      const txt = (script.textContent || '').trim();
      const parsed = txt ? safeJsonParse(txt) : null;
      if (parsed && typeof parsed === 'object') return parsed;
    }

    const raw = (ROOT.dataset.envJson || '').trim();
    const parsed = raw ? safeJsonParse(raw) : null;
    if (parsed && typeof parsed === 'object') return parsed;

    return null;
  }

  async function initOne(ROOT) {
    if (!ROOT || ROOT.__envInitDone) return;
    ROOT.__envInitDone = true;

    const proxy = ROOT.dataset.proxyUrl || DEFAULT_PROXY;
    const sku = (ROOT.dataset.variantSku || '').trim();

    // 1) Liquid inline JSON
    const initial = readInlineJson(ROOT);
    if (isValidEnvPayload(initial)) {
      setStatus(ROOT, 'Données disponibles.');
      renderEnv(ROOT, initial);
      return;
    }

    // 2) Pas de SKU
    if (!sku) {
      setStatus(ROOT, 'SKU indisponible sur cette variante.');
      renderEnv(ROOT, null);
      return;
    }

    // 3) Proxy
    setStatus(ROOT, 'Chargement…');

    try {
      const res = await fetchJSON(buildProxyUrl(proxy, sku));
      const payload = extractPayloadFromProxy(res);

      if (isValidEnvPayload(payload)) {
        setStatus(ROOT, res?.status === 'stored' ? 'Données récupérées.' : 'Données disponibles.');
        renderEnv(ROOT, payload);
        return;
      }

      if (res?.status === 'already_set') {
        // Si ton EF ne renvoie pas le payload en already_set, on ne peut pas afficher sans refresh
        setStatus(ROOT, 'Données enregistrées (rafraîchissez la page).');
        renderEnv(ROOT, null);
        return;
      }

      setStatus(ROOT, 'Aucune donnée.');
      renderEnv(ROOT, null);
    } catch (e) {
      console.warn('[env-widget] proxy error', e);
      setStatus(ROOT, 'Erreur de chargement.');
      renderEnv(ROOT, null);
    }
  }

  function initAll(context = document) {
    context.querySelectorAll('.env-root').forEach(initOne);
  }

  // Boot
  document.addEventListener('DOMContentLoaded', () => initAll(document));

  // Init à l’ouverture drawer/popover (ton thème utilise aria-controls)
  window.addEventListener('click', (e) => {
    const btn = e.target && e.target.closest && e.target.closest('button[is="toggle-button"][aria-controls]');
    if (!btn) return;

    const targetId = btn.getAttribute('aria-controls');
    if (!targetId) return;

    const target = document.getElementById(targetId);
    if (!target) return;

    initAll(target);
  }, { capture: true, passive: true });
})();


//=========================================================================================
// GALERIE PRODUIT MOSAIQUE (style decathlon.fr, sans Flickity)
// - Desktop : mosaïque CSS 2 colonnes (1re image = héros pleine largeur)
// - Mobile  : scroll horizontal natif (défilement libre + peek)
// - Clic / tap sur une image => zoom plein écran (PhotoSwipe : product-image-zoom)
// - Filtrage par variante (#alt) : maj des classes is-filtered / is-selected
// Snippet : snippets/product-media-mosaic.liquid | CSS : custom-product-gallery.css
//=========================================================================================
(function () {
  function toId(el) {
    var v = parseInt(el.getAttribute('data-media-id'), 10);
    return isNaN(v) ? null : v;
  }

  function items(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('.product__media-item'));
  }

  function firstVisible(gallery) {
    return items(gallery).filter(function (i) {
      return !i.classList.contains('is-filtered');
    })[0] || null;
  }

  // Motif decathlon.fr (desktop, via CSS) : 2 petites, 1 grande pleine largeur, 2 petites…
  // en boucle. La grande est donc la 3e photo visible (index 2, 5, 8…).
  // On ne compte que les photos visibles (non filtrées par la variante).
  function updateFeature(gallery) {
    var visibleIndex = 0;
    items(gallery).forEach(function (i) {
      if (i.classList.contains('is-filtered')) {
        i.classList.remove('is-grid-feature');
        return;
      }
      i.classList.toggle('is-grid-feature', visibleIndex % 3 === 2);
      visibleIndex++;
    });
  }

  // Recale la position de la photo sélectionnée (mobile, scroll natif)
  function scrollToSelected(gallery) {
    if (window.matchMedia('(min-width: 1000px)').matches) return; // desktop = grille
    var list = gallery.querySelector('.product__media-list');
    var sel = gallery.querySelector('.product__media-item.is-selected');
    if (!list || !sel) return;
    try {
      list.scrollTo({ left: sel.offsetLeft - list.offsetLeft, behavior: 'smooth' });
    } catch (e) {
      list.scrollLeft = sel.offsetLeft - list.offsetLeft;
    }
  }

  function setSelected(gallery, mediaId) {
    items(gallery).forEach(function (i) {
      var on = mediaId != null && toId(i) === mediaId;
      i.classList.toggle('is-selected', on);
      i.classList.toggle('is-initial-selected', on);
    });
  }

  // Photo "highlight" = variante sélectionnée : on place sa photo en 1re position.
  // Desktop => elle devient la grande image héros (1re du motif) ; mobile => 1re du carrousel.
  function moveSelectedToFront(gallery, selId) {
    var list = gallery.querySelector('.product__media-list');
    if (!list || selId == null) return;
    var sel = null, all = items(gallery);
    for (var k = 0; k < all.length; k++) {
      if (toId(all[k]) === selId) { sel = all[k]; break; }
    }
    if (sel && list.firstElementChild !== sel) {
      list.insertBefore(sel, list.firstElementChild);
    }
  }

  // Photos affichées au départ sur desktop (motif 2 + 1 + 2 = 5) + pas de révélation.
  var INITIAL_DESKTOP_MEDIA = 5;
  var SHOW_MORE_STEP = 4; // chaque clic sur "N Plus" révèle 4 photos de plus (progressif)

  // Libellé : s'il reste des photos masquées => "N Plus" (N restantes) ; sinon => "Voir moins".
  function setShowMoreLabel(gallery, btn, remaining) {
    var label = btn.querySelector('.product__media-show-more-label') || btn;
    if (remaining > 0) {
      label.textContent = remaining + ' ' + (btn.getAttribute('data-label-more') || 'Plus');
    } else {
      label.textContent = btn.getAttribute('data-label-less') || 'Voir moins';
    }
  }

  // "Voir plus" progressif (desktop) : gallery.__shown = nb de photos affichées.
  // On révèle SHOW_MORE_STEP photos de plus à chaque clic (évite de tout déplier d'un coup
  // et de devoir scroller tout pour replier). Mobile = carrousel => pas de limite.
  // (Aucune hauteur en inline : masquage par classes .is-clamped / .is-overflow.)
  function updateShowMore(gallery) {
    var btn = gallery.querySelector('.product__media-show-more');
    var visible = items(gallery).filter(function (i) { return !i.classList.contains('is-filtered'); });

    items(gallery).forEach(function (i) { i.classList.remove('is-overflow'); });

    if (!window.matchMedia('(min-width: 1000px)').matches) {
      gallery.classList.remove('is-clamped');
      if (btn) btn.hidden = true;
      return;
    }

    if (gallery.__shown == null) gallery.__shown = INITIAL_DESKTOP_MEDIA;
    var shown = Math.min(gallery.__shown, visible.length);
    var remaining = visible.length - shown;

    if (visible.length > INITIAL_DESKTOP_MEDIA) {
      visible.forEach(function (i, idx) { if (idx >= shown) i.classList.add('is-overflow'); });
      gallery.classList.toggle('is-clamped', remaining > 0);
      if (btn) { btn.hidden = false; setShowMoreLabel(gallery, btn, remaining); }
    } else {
      gallery.classList.remove('is-clamped');
      if (btn) btn.hidden = true;
    }
  }

  function setupShowMore(gallery) {
    var btn = gallery.querySelector('.product__media-show-more');
    if (btn && !btn.__bound) {
      btn.__bound = true;
      btn.addEventListener('click', function () {
        var visible = items(gallery).filter(function (i) { return !i.classList.contains('is-filtered'); });
        if (gallery.__shown == null) gallery.__shown = INITIAL_DESKTOP_MEDIA;
        if (gallery.__shown < visible.length) {
          gallery.__shown = Math.min(gallery.__shown + SHOW_MORE_STEP, visible.length); // +5
          btn.setAttribute('aria-expanded', 'true');
        } else {
          gallery.__shown = INITIAL_DESKTOP_MEDIA; // tout affiché => on replie au set initial
          btn.setAttribute('aria-expanded', 'false');
          var top = gallery.getBoundingClientRect().top + window.pageYOffset - 100;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }
        updateShowMore(gallery);
      });
    }
    updateShowMore(gallery);
  }

  // Applique le filtrage des photos pour la variante sélectionnée
  function applyVariant(gallery, variant) {
    var optionNames;
    try {
      optionNames = JSON.parse(gallery.getAttribute('data-product-options') || '[]');
    } catch (e) {
      optionNames = [];
    }
    optionNames = optionNames.map(function (s) { return String(s).toLowerCase(); });

    var featuredId = variant && variant.featured_media ? variant.featured_media.id : null;

    items(gallery).forEach(function (item) {
      var fOpt = item.getAttribute('data-filter-option');
      var fVal = item.getAttribute('data-filter-value');
      var filtered = false;

      if (fOpt && variant && variant.options) {
        var idx = optionNames.indexOf(String(fOpt).toLowerCase());
        if (idx > -1) {
          var selVal = String(variant.options[idx] || '').toLowerCase();
          if (selVal !== String(fVal).toLowerCase()) {
            // On garde quand même la photo "featured" de la variante
            if (!(featuredId && toId(item) === featuredId)) {
              filtered = true;
            }
          }
        }
      }
      item.classList.toggle('is-filtered', filtered);
    });

    // Sélection : photo featured de la variante, sinon 1re photo visible
    var selId = featuredId;
    if (!selId) {
      var fv = firstVisible(gallery);
      selId = fv ? toId(fv) : null;
    }
    setSelected(gallery, selId);
    moveSelectedToFront(gallery, selId); // highlight = photo de la variante choisie
    updateFeature(gallery);
    scrollToSelected(gallery);
    gallery.__shown = INITIAL_DESKTOP_MEDIA; // on repart du set initial à chaque changement de variante
    updateShowMore(gallery); // le nombre de photos visibles change quand on filtre par variante
  }

  // Skeleton loader : tant que l'<img> n'est pas peinte, le wrapper affiche un shimmer
  // (CSS). On pose .is-loaded sur le wrapper dès que l'image a fini de charger (ou si
  // elle est déjà en cache => img.complete). N'altère ni le filtrage, ni le N-Plus, ni le zoom.
  function markLoaded(img) {
    var wrapper = img.closest('.product__media-image-wrapper');
    if (wrapper) wrapper.classList.add('is-loaded');
  }

  function setupSkeleton(gallery) {
    var imgs = gallery.querySelectorAll('.product__media-img');
    Array.prototype.forEach.call(imgs, function (img) {
      if (img.__skelBound) return;
      img.__skelBound = true;
      // naturalWidth > 0 confirme une image réellement décodée (complete peut être vrai
      // pour une image cassée/non chargée).
      if (img.complete && img.naturalWidth > 0) {
        markLoaded(img);
        return;
      }
      img.addEventListener('load', function () { markLoaded(img); }, { once: true });
      // En cas d'erreur on retire quand même le skeleton (évite un shimmer infini).
      img.addEventListener('error', function () { markLoaded(img); }, { once: true });
    });
  }

  function openZoomAt(gallery, item) {
    setSelected(gallery, toId(item));
    var zoom = gallery.querySelector('product-image-zoom');
    if (zoom) {
      try { zoom.open = true; } catch (e) { zoom.setAttribute('open', ''); }
    }
  }

  function initGallery(gallery) {
    if (gallery.__mosaicInit) return;
    gallery.__mosaicInit = true;

    // Highlight initial : photo de la variante sélectionnée au chargement (mobile + desktop)
    var initialSelected = gallery.querySelector('.product__media-item.is-selected, .product__media-item.is-initial-selected');
    if (initialSelected) moveSelectedToFront(gallery, toId(initialSelected));
    updateFeature(gallery);
    setupShowMore(gallery);
    setupSkeleton(gallery);

    // Clic / tap sur une image => zoom (le scroll-drag mobile ne déclenche pas de click)
    var list = gallery.querySelector('.product__media-list');
    if (list) {
      list.addEventListener('click', function (e) {
        var item = e.target.closest('.product__media-item');
        if (!item || item.classList.contains('is-filtered')) return;
        if (item.getAttribute('data-media-type') !== 'image') return;
        openZoomAt(gallery, item);
      });
    }

    // Changement de variante (on écoute le formulaire, comme le thème natif)
    var formId = gallery.getAttribute('data-form-id');
    var form = formId ? document.getElementById(formId) : null;
    var onVariant = function (e) {
      if (!e.detail || !e.detail.variant) return;
      applyVariant(gallery, e.detail.variant);
    };
    if (form) {
      form.addEventListener('variant:changed', onVariant);
    } else {
      document.addEventListener('variant:changed', onVariant);
    }
  }

  function initAllGalleries() {
    document.querySelectorAll('.product__media--mosaic').forEach(initGallery);
  }

  function refreshAllShowMore() {
    document.querySelectorAll('.product__media--mosaic').forEach(updateShowMore);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }

  // Les hauteurs ne sont fiables qu'une fois les images chargées
  window.addEventListener('load', refreshAllShowMore);

  // Re-mesure au redimensionnement (bascule mobile/desktop incluse), avec un léger debounce
  var mosaicResizeTimer = null;
  window.addEventListener('resize', function () {
    if (mosaicResizeTimer) clearTimeout(mosaicResizeTimer);
    mosaicResizeTimer = setTimeout(refreshAllShowMore, 200);
  });
})();
